import { useEffect, useRef, useState } from "react";

export const useWebSocket = (url, options = {}) => {
  const { enabled = true, defer = false } = options;
  const socketRef = useRef(null);
  const sendTimeRef = useRef(null);
  const requestIdRef = useRef(0); // Track request IDs
  const latestRequestIdRef = useRef(0); // Track the latest request ID
  const pendingRequestsRef = useRef(new Set()); // Track ALL pending request IDs
  const [objData, setObjData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Defer WebSocket connection to avoid blocking initial render
    const connectWebSocket = () => {
      if (socketRef.current) return; // Already connected

      console.log("🔌 Initiating WebSocket connection...");
      socketRef.current = new WebSocket(url);

      socketRef.current.onopen = () => {
        console.log("✅ WebSocket verbinding geopend");
        setIsConnected(true);
      };

      socketRef.current.onmessage = (event) => {
        const receiveTime = performance.now();

        try {
          const data = JSON.parse(event.data);

          // Check if this response is for the latest request (using client-side tracking)
          const requestId = data.requestId;

          // Try to infer the request ID if backend doesn't return it
          // Use the oldest pending request (FIFO - first response likely matches first request)
          let effectiveRequestId = requestId;
          if (effectiveRequestId === undefined && pendingRequestsRef.current.size > 0) {
            effectiveRequestId = Math.min(...Array.from(pendingRequestsRef.current));
          }

          // CRITICAL: Only accept responses that match the LATEST request
          if (effectiveRequestId !== undefined && effectiveRequestId !== latestRequestIdRef.current) {
            console.log(`⏭️ Ignoring stale/non-latest response (ID: ${effectiveRequestId}, latest: ${latestRequestIdRef.current}, pending: [${Array.from(pendingRequestsRef.current).join(', ')}])`);
            // Remove from pending set
            pendingRequestsRef.current.delete(effectiveRequestId);
            return; // Ignore stale responses
          }

          if (data.type === "obj") {
            // Remove this request from pending set
            if (effectiveRequestId !== undefined) {
              pendingRequestsRef.current.delete(effectiveRequestId);
              console.log(`✅ Accepted response (ID: ${effectiveRequestId}), remaining pending: [${Array.from(pendingRequestsRef.current).join(', ')}]`);
            }

            // Wrap objData with metadata to track request ID
            setObjData({
              content: data.content,
              requestId: effectiveRequestId,
              timestamp: Date.now()
            });
            console.log(`📥 Received OBJ from backend (ID: ${effectiveRequestId})`);
            if (sendTimeRef.current) {
              const duration = (receiveTime - sendTimeRef.current).toFixed(2);
              console.log(`⏱️ Backend response time: ${duration} ms`);
              setMessages((prev) => [...prev, `Reactietijd voor OBJ: ${duration} ms`]);
              sendTimeRef.current = null;
            }
          } else {
            console.log('📥 Received message from backend:', event.data);
            setMessages((prev) => [...prev, "Server: " + event.data]);
            if (sendTimeRef.current) {
              const duration = (receiveTime - sendTimeRef.current).toFixed(2);
              console.log(`⏱️ Backend response time: ${duration} ms`);
              setMessages((prev) => [...prev, `Reactietijd voor bericht: ${duration} ms`]);
              sendTimeRef.current = null;
            }
          }
        } catch (e) {
          console.log('📥 Received raw data from backend:', event.data);
          setMessages((prev) => [...prev, "Server (raw): " + event.data]);
          if (sendTimeRef.current) {
            const duration = (receiveTime - sendTimeRef.current).toFixed(2);
            console.log(`⏱️ Backend response time: ${duration} ms`);
            setMessages((prev) => [...prev, `Reactietijd (raw): ${duration} ms`]);
            sendTimeRef.current = null;
          }
        }
      };

      socketRef.current.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
      };

      socketRef.current.onclose = () => {
        console.log("❌ WebSocket verbinding gesloten");
        setIsConnected(false);
      };
    };

    if (defer) {
      // Defer connection until after initial render and page load
      const timeoutId = setTimeout(connectWebSocket, 100);
      return () => {
        clearTimeout(timeoutId);
        socketRef.current?.close();
      };
    } else {
      connectWebSocket();
      return () => {
        socketRef.current?.close();
      };
    }
  }, [url, enabled, defer]);

  const sendMessage = (data) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // Increment request ID and attach it to the message
      requestIdRef.current += 1;
      latestRequestIdRef.current = requestIdRef.current;
      const currentRequestId = requestIdRef.current;

      // Add this request to the pending set
      pendingRequestsRef.current.add(currentRequestId);

      const messageWithId = { ...data, requestId: currentRequestId };

      sendTimeRef.current = performance.now();
      socketRef.current.send(JSON.stringify(messageWithId));
      console.log(`📤 Sending to backend (ID: ${currentRequestId}), pending: [${Array.from(pendingRequestsRef.current).join(', ')}]`);
      setMessages((prev) => [...prev, "Jij: " + JSON.stringify(data)]);

      // Return the request ID so caller can track it
      return currentRequestId;
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send:', data);
      return null;
    }
  };

  return { objData, messages, isConnected, sendMessage, latestRequestId: latestRequestIdRef };
};
